import { fetchJSON } from '@bonnet/next/fetch';

import { createSelector, DuckSelector, objectDuckCreator } from '@atc/modular-redux';

import { bapDuckPreset } from '@/ducks/presets';

const bapConfigurationDuck = objectDuckCreator({
    ...bapDuckPreset,
    store: 'activeConfiguration',
    initialState: {
        isValidConfig: true,
        loading: true,
        warnings: [],
    },
}).extend({
    creators: () => ({
        loadConfigurationData: ({ allTrimOptions, selectedOptions, vehicleId }) => async (dispatch) => {
            // set loading to true while configuration service is fetched
            dispatch(bapConfigurationDuck?.creators?.setKeys({
                loading: true,
            }));

            // determine if both or no colors should be sent to configuration service, when there is only 1 color
            // selected it may be redundant to show a warning
            let unselectedColors = 0;
            Object.entries(selectedOptions).forEach(([key, value]) => {
                if ((key === 'exteriorColorList' || key === 'interiorColorList') && value === null) {
                    unselectedColors += 1;
                }
            });

            // convert selected options into list of vehicle option id's send to configuration service
            const selectedOptionIds = [];
            Object.entries(selectedOptions).forEach(([key, value]) => {
                const excludeColor = unselectedColors === 1 && (key === 'exteriorColorList' || key === 'interiorColorList');
                if (value?.vehicleOptionId && key !== 'packageList' && !excludeColor) {
                    selectedOptionIds.push(value?.vehicleOptionId);
                }
            });

            let isValidConfiguration = false;
            const configurationWarnings = [];
            const warningIds = {};

            if (vehicleId && selectedOptionIds.length > 0) {
                const {
                    isValid,
                    warnings = [],
                } = await fetchJSON('/cars-for-sale/vrs/config/validateconfiguration', {
                    circuitBreaker: {
                        id: 'vrsvalidateconfiguration',
                        timeout: 5000,
                        resetTimeout: 30000,
                        fallback: {},
                    },
                    headers: {
                        'X-fwd-svc': 'atc',
                    },
                    method: 'POST',
                    query: {
                        startingConfiguration: {
                            vehicleId,
                            vehicleOptionIds: selectedOptionIds,
                        },
                    },
                });

                // filter out duplicate warnings & warnings related to packages
                warnings.forEach((warning) => {
                    // TODO delete the removal of package warning logic once NCCO General Availability feature is ready
                    const updatedChildIds = [];
                    allTrimOptions.forEach((option) => {
                        if (option.optionType !== 'Option' && warning.childIds.includes(option.vehicleOptionId)) {
                            updatedChildIds.push(option.vehicleOptionId);
                        }
                    });

                    if (warning?.code === 'Configuration.SelectOneChildId' && updatedChildIds.length > 0) {
                        const updatedWarning = {
                            childIds: updatedChildIds,
                            code: warning.code,
                            message: warning.message,
                            parentId: warning.parentId,
                        };
                        // store warnings using parentId as the key and overwrite any duplicate warnings with same
                        // parentId to get most up-to-date warning
                        Object.assign(warningIds, { [warning.parentId]: updatedWarning });
                    }
                });

                // convert unique map of warnings back into an array
                Object.values(warningIds).forEach((value) => {
                    configurationWarnings.push(value);
                });

                isValidConfiguration = isValid;
            }

            dispatch(bapConfigurationDuck?.creators?.setKeys({
                isValidConfig: isValidConfiguration,
                loading: false,
                warnings: configurationWarnings,
            }));
        },
    }),
    selectors: (duck) => ({
        getActiveConfiguration: new DuckSelector((selectors) => createSelector(
            selectors.getDuckState,
            (state) => state
        )),
    }),
});

export default bapConfigurationDuck;
